import '@angular/localize';
import { ABSTRACT_BG_IMAGES } from '@rapider/definitions';
import { create } from '@rapider/sdk/lib/fn/comment-controller/create';

export const WORKSPACE_CONFIG = {
  texts: {
    selectTitle: $localize`:@@workspace.selectTitle:Select a Workspace`,
    selectSubtitle: $localize`:@@workspace.selectSubtitle:Choose a workspace to continue your work.`,
    createTitle: $localize`:@@workspace.createTitle:Create New Workspace`,
    createSubtitle: $localize`:@@workspace.createSubtitle:Set up a new workspace to organize your projects.`,
    nameLabel: $localize`:@@workspace.nameLabel:Workspace Name`,
    namePlaceholder: $localize`:@@workspace.namePlaceholder:e.g., Aviation Maintenance AI`,
    keyLabel: $localize`:@@workspace.keyLabel:Workspace Key`,
    keyPlaceholder: $localize`:@@workspace.keyPlaceholder:e.g., aviation-maintenance-ai`,
    descriptionLabel: $localize`:@@workspace.descriptionLabel:Description (Optional)`,
    descriptionPlaceholder: $localize`:@@workspace.descriptionPlaceholder:Enter a brief overview of this workspace...`,
    createButton: $localize`:@@workspace.createButton:Create Workspace`,
    emptyState: $localize`:@@workspace.emptyState:No workspaces found. Create one to get started.`,
    createdBy: $localize`:@@workspace.createdBy:Created by`,
    onDate: $localize`:@@workspace.onDate:on`,
    goToWorkspace: $localize`:@@workspace.goToWorkspace:Go to Workspace`,
    searchPlaceholder: $localize`:@@workspace.searchPlaceholder:Search workspaces...`,
    sortAlphabetical: $localize`:@@workspace.sortAlphabetical:Alphabetical (A-Z)`,
    sortDateDesc: $localize`:@@workspace.sortDateDesc:Newest First`,
    sortDateAsc: $localize`:@@workspace.sortDateAsc:Oldest First`,
    filterAll: $localize`:@@workspace.filterAll:All Workspaces`,
    filterFavorited: $localize`:@@workspace.filterFavorited:Favorited`,
    filterShared: $localize`:@@workspace.filterShared:Shared with Me`,
    filterOwned: $localize`:@@workspace.filterOwned:Owned by Me`,
    loadMore: $localize`:@@workspace.loadMore:Load More Workspaces`,
    removeFavorite: $localize`:@@workspace.removeFavorite:Remove Favorite`,
    markFavorite: $localize`:@@workspace.markFavorite:Mark as Favorite`,
    
    // --- Settings Page Texts ---
    settingsTitle: $localize`:@@workspace.settingsTitle:Workspace Settings`,
    settingsSubtitle: $localize`:@@workspace.settingsSubtitle:Manage your workspace details, features, and team members.`,
    infoSectionTitle: $localize`:@@workspace.infoSectionTitle:Workspace Information`,
    infoKey: $localize`:@@workspace.infoKey:Workspace Key`,
    infoId: $localize`:@@workspace.infoId:Workspace ID`,
    infoCreated: $localize`:@@workspace.infoCreated:Created Date`,
    infoUpdated: $localize`:@@workspace.infoUpdated:Last Updated`,
    infoAiTokens: $localize`:@@workspace.infoAiTokens:AI Tokens Used`,
    infoAiCost: $localize`:@@workspace.infoAiCost:AI Tokens Cost`,
    infoAiProviders: $localize`:@@workspace.infoAiProviders:Available AI Providers`,
    generalSectionTitle: $localize`:@@workspace.generalSectionTitle:General Settings`,
    modulesSectionTitle: $localize`:@@workspace.modulesSectionTitle:Feature Modules`,
    authModuleLabel: $localize`:@@workspace.authModuleLabel:Authentication Module`,
    authModuleDesc: $localize`:@@workspace.authModuleDesc:Enable user authentication, registration, and session management.`,
    paymentModuleLabel: $localize`:@@workspace.paymentModuleLabel:Payment Module`,
    paymentModuleDesc: $localize`:@@workspace.paymentModuleDesc:Enable subscription and payment processing capabilities.`,
    templatesSectionTitle: $localize`:@@workspace.templatesSectionTitle:Code Generation Templates`,
    membersSectionTitle: $localize`:@@workspace.membersSectionTitle:Workspace Members`,
    membersTableUser: $localize`:@@workspace.membersTableUser:User`,
    membersTableRole: $localize`:@@workspace.membersTableRole:Role`,
    membersTableStatus: $localize`:@@workspace.membersTableStatus:Status`,
    dangerSectionTitle: $localize`:@@workspace.dangerSectionTitle:Danger Zone`,
    deleteWorkspaceTitle: $localize`:@@workspace.deleteWorkspaceTitle:Delete Workspace`,
    deleteWorkspaceDesc: $localize`:@@workspace.deleteWorkspaceDesc:Once you delete a workspace, there is no going back. Please be certain.`,
    deleteConfirmLabel: $localize`:@@workspace.deleteConfirmLabel:Please type the workspace key to confirm:`,
    deleteButton: $localize`:@@workspace.deleteButton:Delete Workspace`,
    saveChanges: $localize`:@@workspace.saveChanges:Save Changes`,
  },
  notifications: {
    authFailed: $localize`:@@workspace.authFailed:Authentication failed. Please try logging in again.`,
    fetchFailed: $localize`:@@workspace.fetchFailed:Failed to load workspaces.`,
    createSuccess: $localize`:@@workspace.createSuccess:Workspace created successfully.`,
    createFailed: $localize`:@@workspace.createFailed:Could not create workspace.`,
    favoriteSuccess: $localize`:@@workspace.favoriteSuccess:Workspace marked as favorite.`,
    unfavoriteSuccess: $localize`:@@workspace.unfavoriteSuccess:Workspace removed from favorites.`,
    updateSuccess: $localize`:@@workspace.updateSuccess:Workspace updated successfully.`,
    updateFailed: $localize`:@@workspace.updateFailed:Failed to update workspace.`,
    deleteSuccess: $localize`:@@workspace.deleteSuccess:Workspace deleted successfully.`,
    deleteFailed: $localize`:@@workspace.deleteFailed:Failed to delete workspace.`,
    idCopiedSuccess: $localize`:@@workspace.idCopiedSuccess:Workspace ID copied to clipboard.`,
  }
};

/**
 * Static configuration for Code Generation Templates to ensure we map internal IDs to UI elements properly.
 */
export const CODE_GENERATION_TEMPLATES = [
  { 
    id: 'e7596fd3-4b7b-4b98-a7c9-aa6725e092a3', 
    name: $localize`:@@workspace.templateApiName:API Server`, 
    description: $localize`:@@workspace.templateApiDesc:Backend Node.js API with Loopback 4` 
  },
  { 
    id: '2004e00e-767c-4e01-920e-e5dca0980275', 
    name: $localize`:@@workspace.templateMobileName:Mobile App`, 
    description: $localize`:@@workspace.templateMobileDesc:Flutter & Dart based cross-platform mobile application` 
  },
  { 
    id: 'a008576c-3a1f-41eb-b3e3-9829b87e3cd8', 
    name: $localize`:@@workspace.templateWebName:Web App`, 
    description: $localize`:@@workspace.templateWebDesc:Angular based frontend web application` 
  }
];

/**
 * Pure function to check if a workspace key matches the confirmation input string exactly.
 * @param {string} actualKey - The real workspace key.
 * @param {string} inputKey - The user's input string.
 * @returns {boolean} True if they match.
 */
export function isDeleteKeyMatch(actualKey: string, inputKey: string): boolean {
  if (!actualKey || !inputKey) return false;
  return actualKey === inputKey.trim();
}

/**
 * Pure function to add or remove a code template ID from the active list.
 * @param {string} templateId - The ID of the template being toggled.
 * @param {string[]} currentIds - The existing list of active template IDs.
 * @param {boolean} isActive - Whether the toggle was turned ON (true) or OFF (false).
 * @returns {string[]} A new array containing the updated active IDs.
 */
export function toggleCodeTemplateId(templateId: string, currentIds: string[] = [], isActive: boolean): string[] {
  const uniqueIds = new Set(currentIds || []);
  if (isActive) {
    uniqueIds.add(templateId);
  } else {
    uniqueIds.delete(templateId);
  }
  return Array.from(uniqueIds);
}

/**
 * Pure function to build a dynamic LoopBack filter for the projects query.
 * @param {number} pageSize - Maximum number of projects to return.
 * @param {number} skip - Number of projects to skip for pagination.
 * @param {string} searchQuery - The text to search for within project names.
 * @param {string} sortCriteria - How to sort the results ('dateDesc', 'dateAsc', 'alphabetical').
 * @param {string} filterCriteria - How to filter ('all', 'favorited', 'shared', 'owned').
 * @param {Set<string>} favoriteIds - A set of known favorited project IDs.
 * @param {string | null} currentUserId - The ID of the current user.
 * @returns {string} Stringified JSON filter for the API.
 */
export function buildDynamicProjectsFilter(
  pageSize: number,
  skip: number,
  searchQuery: string,
  sortCriteria: string,
  filterCriteria: string,
  favoriteIds: Set<string>,
  currentUserId: string | null = null
): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filter: any = {
    fields: {
      id: true,
      name: true,
      createdDate: true,
      createdBy: true,
      createdById: true
    },
    limit: pageSize,
    skip: skip,
    where: {}
  };

  // 1. Handle Sorting
  switch (sortCriteria) {
    case 'alphabetical':
      filter.order = ["name ASC"];
      break;
    case 'dateAsc':
      filter.order = ["createdDate ASC"];
      break;
    case 'dateDesc':
    default:
      filter.order = ["createdDate DESC"];
      break;
  }

  // 2. Handle Where Conditions
  const andConditions: any[] = [];

  // Search by name
  if (searchQuery && searchQuery.trim() !== '') {
    // Note: Assuming standard LoopBack 4 regex or like syntax.
    andConditions.push({ name: { like: `.*${searchQuery.trim()}.*`, options: 'i' } });
  }

  // Segment by ownership or favorites
  if (filterCriteria === 'owned' && currentUserId) {
    andConditions.push({ createdById: currentUserId });
  } else if (filterCriteria === 'shared' && currentUserId) {
    andConditions.push({ createdById: { neq: currentUserId } });
  } else if (filterCriteria === 'favorited') {
    const favArray = Array.from(favoriteIds);
    if (favArray.length > 0) {
      andConditions.push({ id: { inq: favArray } });
    } else {
      // If filtering by favorites but none exist, ensure it returns empty.
      andConditions.push({ id: 'NONE_EXISTING_ID_PLACEHOLDER' });
    }
  }

  if (andConditions.length > 0) {
    filter.where = { and: andConditions };
  }

  return JSON.stringify(filter);
}

/**
 * Pure function to build the filter for fetching favorited workspaces on initialization.
 * @param {number} limit - Maximum number of records to return (default 20 as requested).
 * @param {number} skip - Number of records to skip.
 * @returns {string} Stringified JSON filter for the API.
 */
export function buildFavoriteProjectsFilter(limit: number = 20, skip: number = 0): string {
  const filter = {
    fields: { id: true, isFavorited: true },
    limit,
    skip,
    where: { isFavorited: true }
  };
  return JSON.stringify(filter);
}

/**
 * Pure function to extract workspace IDs from the favorited response array.
 * @param {any[]} favorites - The list of favorited objects from the API.
 * @returns {string[]} An array of strictly string workspace IDs.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractFavoriteIds(favorites: any[]): string[] {
  if (!Array.isArray(favorites)) return [];
  return favorites.filter(f => f.id).map(f => f.id);
}

/**
 * Pure function to automatically generate a valid, URL-safe workspace key from a name.
 * @param {string} name - The raw workspace name provided by the user.
 * @returns {string} The formatted workspace key.
 */
export function generateWorkspaceKey(name: string): string {
  if (!name) return '';
  return name
    .trim()
    .toLowerCase()
    // Replace one or more spaces with a single hyphen
    .replace(/\s+/g, '-')
    // Remove all characters that are not lowercase alphanumeric or hyphens
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Pure function to extract the active Person ID from a User response.
 * @param {any} user - The user object returned from the API.
 * @returns {string | null} The ID of the first person profile, or null.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractPersonId(user: any): string | null {
  if (user && user.people && Array.isArray(user.people) && user.people.length > 0) {
    return user.people[0].id;
  }
  return null;
}

/**
 * Pure function to deterministically assign an abstract background image based on a project ID.
 * Output strictly depends on the input string to ensure the same project always gets the same image.
 * @param {string} id - The unique project ID.
 * @param {string[]} images - The array of available image paths.
 * @returns {string} A CSS-compatible URL string for the background image.
 */
export function getDeterministicCardImage(id: string, images: string[] = ABSTRACT_BG_IMAGES): string {
  if (!id || !images || images.length === 0) return '';

  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % images.length;
  return images[index];
}